'use client';

import React from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

type Kind = 'avatar' | 'banner';

export default function ImageEditorModal({
  file,
  kind,
  onCancel,
  onExport,
  aspect: aspectOverride,
  output,
  saving,
  title,
  hideFilters,
}: {
  file: File;
  kind: Kind;
  onCancel: () => void;
  onExport: (images: { main: Blob; thumb?: Blob; small?: Blob }) => void;
  aspect?: number;
  output?: { main: { w: number; h: number; q?: number }; thumb?: { w: number; h: number; q?: number }; small?: { w: number; h: number; q?: number } };
  saving?: boolean;
  title?: string;
  hideFilters?: boolean;
}) {
  const aspect = typeof aspectOverride === 'number' && aspectOverride > 0 ? aspectOverride : (kind === 'avatar' ? 1 : 3); // 3:1 banner default
  const previewW = kind === 'avatar' ? 360 : 540; // CSS pixels for preview canvas (reasonable size)
  const previewH = Math.round(previewW / aspect);

  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const imgRef = React.useRef<ImageBitmap | HTMLImageElement | null>(null);
  const [imgDims, setImgDims] = React.useState<{ w: number; h: number } | null>(null);
  const [zoom, setZoom] = React.useState(1);
  const [angle, setAngle] = React.useState(0); // multiples of 90 are ideal
  const [offset, setOffset] = React.useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const dragRef = React.useRef<{ dragging: boolean; x: number; y: number }>({ dragging: false, x: 0, y: 0 });

  // New filter controls
  type FilterType = "none" | "bw" | "sepia" | "vintage" | "vibrant";
  const [filter, setFilter] = React.useState<FilterType>("none");
  const [brightness, setBrightness] = React.useState(100);
  const [contrast, setContrast] = React.useState(100);

  const FILTERS: Record<FilterType, string> = {
    none: "",
    bw: "grayscale(100%)",
    sepia: "sepia(80%)",
    vintage: "sepia(50%) contrast(120%) saturate(80%)",
    vibrant: "saturate(150%) contrast(110%)",
  };

  const [loadError, setLoadError] = React.useState<string | null>(null);
  React.useEffect(() => {
    let active = true;
    setLoadError(null);
    // 15s cap: a decode that never settles left imgRef null forever, which
    // made Save a silent no-op — the same no-settle class as the video
    // poster capture fixed in videoThumb.ts (2026-07-24 sweep).
    const cap = setTimeout(() => {
      if (active && !imgRef.current) setLoadError('We could not load this image. Try a different file.');
    }, 15_000);
    const loadViaImg = () => {
      const img = new Image();
      (img as any).style = { imageOrientation: 'from-image' };
      img.onload = () => { if (!active) return; imgRef.current = img; setImgDims({ w: img.naturalWidth, h: img.naturalHeight }); };
      img.onerror = () => { if (active) setLoadError('We could not load this image. Try a different file.'); };
      img.src = URL.createObjectURL(file);
    };
    (async () => {
      try {
        if ('createImageBitmap' in window) {
          const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' as any });
          if (!active) return;
          imgRef.current = bmp;
          setImgDims({ w: bmp.width, h: bmp.height });
        } else {
          loadViaImg();
        }
      } catch {
        // fallback to basic image element
        loadViaImg();
      }
    })();
    return () => { active = false; clearTimeout(cap); };
  }, [file]);

  // draw preview
  const draw = React.useCallback((ctx: CanvasRenderingContext2D) => {
    const targetW = previewW;
    const targetH = previewH;
    ctx.clearRect(0, 0, targetW, targetH);
    const img = imgRef.current as any;
    if (!img || !imgDims) return;
    const iw = imgDims.w;
    const ih = imgDims.h;
    // base scale to cover
    const baseScale = Math.max(targetW / iw, targetH / ih);
    const scale = baseScale * zoom;
    ctx.save();
    ctx.translate(targetW / 2 + offset.x, targetH / 2 + offset.y);
    ctx.rotate((angle * Math.PI) / 180);
    ctx.scale(scale, scale);
    // Apply filters, brightness, and contrast
    ctx.filter = `${FILTERS[filter]} brightness(${brightness}%) contrast(${contrast}%)`;
    ctx.drawImage(img, -iw / 2, -ih / 2, iw, ih);
    ctx.restore();
  }, [previewW, previewH, zoom, angle, offset.x, offset.y, imgDims, filter, brightness, contrast, FILTERS]);

  React.useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    c.width = previewW; c.height = previewH;
    const ctx = c.getContext('2d'); if (!ctx) return;
    draw(ctx);
  }, [previewW, previewH, draw]);

  // drag handlers
  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    dragRef.current.dragging = true; dragRef.current.x = e.clientX; dragRef.current.y = e.clientY;
    (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
  }
  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!dragRef.current.dragging) return;
    const dx = e.clientX - dragRef.current.x; const dy = e.clientY - dragRef.current.y;
    dragRef.current.x = e.clientX; dragRef.current.y = e.clientY;
    setOffset((o) => ({ x: o.x + dx, y: o.y + dy }));
  }
  function onPointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    dragRef.current.dragging = false;
    try { (e.currentTarget as HTMLCanvasElement).releasePointerCapture(e.pointerId); } catch {}
  }

  function rotate(delta: number) {
    setAngle((a) => {
      let n = a + delta; while (n <= -180) n += 360; while (n > 180) n -= 360; return n;
    });
  }

  function resetView() { setZoom(1); setAngle(0); setOffset({ x: 0, y: 0 }); }

  async function exportImages() {
    const img = imgRef.current as any;
    if (!img || !imgDims) {
      // Never a silent no-op: clicking Save before the image decoded (or
      // after a failed decode) previously did nothing at all.
      setLoadError((prev) => prev || 'Still preparing this image — try Save again in a moment.');
      return;
    }
    const make = (w: number, h: number, q = 0.88): Blob => {
      const c = document.createElement('canvas'); c.width = w; c.height = h;
      const ctx = c.getContext('2d')!;
      // fill white to avoid transparent edges (JPEG)
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, w, h);
      // compute draw identical to preview but mapped to output size
      const iw = imgDims.w; const ih = imgDims.h;
      const baseScale = Math.max(w / iw, h / ih); const scale = baseScale * zoom;
      const scaleX = w / previewW; const scaleY = h / previewH;
      ctx.save();
      ctx.translate(w / 2 + offset.x * scaleX, h / 2 + offset.y * scaleY);
      ctx.rotate((angle * Math.PI) / 180);
      ctx.scale(scale, scale);
      // Apply filters, brightness, and contrast to export
      ctx.filter = `${FILTERS[filter]} brightness(${brightness}%) contrast(${contrast}%)`;
      ctx.drawImage(img, -iw / 2, -ih / 2, iw, ih);
      ctx.restore();
      const data = c.toDataURL('image/jpeg', q);
      const b = atob(data.split(',')[1]);
      const u8 = new Uint8Array(b.length); for (let i = 0; i < b.length; i++) u8[i] = b.charCodeAt(i);
      return new Blob([u8], { type: 'image/jpeg' });
    };

    if (output && output.main) {
      const qMain = output.main.q ?? 0.9;
      const main = make(output.main.w, output.main.h, qMain);
      const images: { main: Blob; thumb?: Blob; small?: Blob } = { main };
      if (output.thumb) {
        const qT = output.thumb.q ?? 0.85;
        images.thumb = make(output.thumb.w, output.thumb.h, qT);
      }
      if (output.small) {
        const qS = output.small.q ?? 0.8;
        images.small = make(output.small.w, output.small.h, qS);
      }
      onExport(images);
    } else if (kind === 'avatar') {
      const main = make(1080, 1080, 0.9);
      const small = make(80, 80, 0.8);
      const thumb = make(400, 400, 0.85);
      onExport({ main, small, thumb });
    } else {
      const main = make(1920, 640, 0.9);
      const thumb = make(480, 160, 0.85);
      onExport({ main, thumb });
    }
  }

  const isSaving = !!saving;

  return (
    <div className="fixed inset-0 z-[1000] bg-black/60 flex items-start md:items-center justify-center p-2 md:p-4 overflow-y-auto">
      <div className={`relative bg-background text-foreground w-full max-w-3xl rounded-xl border shadow-lg my-auto ${isSaving ? 'ring-2 ring-primary/40 animate-pulse' : ''}`} aria-busy={isSaving}>
        <div className="flex items-center justify-between px-4 py-2 md:py-3 border-b">
          <h2 className="text-base md:text-lg font-semibold">{title || `Edit ${kind === 'avatar' ? 'Avatar' : 'Banner'}`}</h2>
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>Close</Button>
        </div>
        <div className={`p-3 md:p-4 ${isSaving ? 'pointer-events-none opacity-60' : ''}`}>
          <div className="mx-auto w-full" style={{ maxWidth: previewW, aspectRatio: `${previewW}/${previewH}` }}>
            <canvas
              ref={canvasRef}
              className="w-full h-full rounded-md bg-black/5 dark:bg-white/10 cursor-grab"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
            />
          </div>
          <div className="mt-3 md:mt-4 space-y-3 md:space-y-4">
            {/* Zoom and Rotation */}
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <label className="text-xs md:text-sm font-medium">Zoom</label>
              <input type="range" min={0.5} max={3} step={0.01} value={zoom} onChange={(e)=>setZoom(parseFloat(e.target.value))} className="flex-1 min-w-[100px] max-w-[200px]" />
              <span className="text-xs text-muted-foreground">{Math.round(zoom * 100)}%</span>
              <div className="flex gap-1.5">
                <Button type="button" variant="secondary" size="sm" onClick={()=>rotate(-90)} disabled={isSaving}>-90°</Button>
                <Button type="button" variant="secondary" size="sm" onClick={()=>rotate(+90)} disabled={isSaving}>+90°</Button>
                <Button type="button" variant="outline" size="sm" onClick={resetView} disabled={isSaving}>Reset</Button>
              </div>
            </div>

            {!hideFilters && (<>
            {/* Filters */}
            <div>
              <label className="text-sm font-medium mb-2 block">Filter</label>
              <div className="flex gap-2 flex-wrap">
                {(["none", "bw", "sepia", "vintage", "vibrant"] as FilterType[]).map((f) => (
                  <Button
                    key={f}
                    type="button"
                    variant={filter === f ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFilter(f)}
                    disabled={isSaving}
                  >
                    {f === "none" ? "None" : f === "bw" ? "B&W" : f.charAt(0).toUpperCase() + f.slice(1)}
                  </Button>
                ))}
              </div>
            </div>

            {/* Brightness */}
            <div>
              <label className="text-sm font-medium mb-1 block">
                Brightness: {brightness}%
              </label>
              <input
                type="range"
                min="50"
                max="150"
                value={brightness}
                onChange={(e) => setBrightness(Number(e.target.value))}
                className="w-full"
                disabled={isSaving}
              />
            </div>

            {/* Contrast */}
            <div>
              <label className="text-sm font-medium mb-1 block">
                Contrast: {contrast}%
              </label>
              <input
                type="range"
                min="50"
                max="150"
                value={contrast}
                onChange={(e) => setContrast(Number(e.target.value))}
                className="w-full"
                disabled={isSaving}
              />
            </div>
            </>)}
          </div>
        </div>
        <div className="sticky bottom-0 px-4 py-2 md:py-3 border-t bg-background flex items-center justify-end gap-2 rounded-b-xl">
          {loadError && <span className="mr-auto self-center text-xs text-destructive">{loadError}</span>}
          <Button variant="secondary" onClick={onCancel} disabled={isSaving}>Cancel</Button>
          <Button variant="primary" onClick={exportImages} disabled={isSaving}>
            {isSaving ? (<><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>) : 'Save'}
          </Button>
        </div>

        {isSaving && (
          <div className="absolute inset-0 rounded-xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-600/10 via-pink-600/10 to-violet-700/10 animate-pulse" />
            <div className="absolute inset-0 backdrop-blur-[2px]" />
            <div className="absolute inset-0 grid place-items-center">
              <div className="flex items-center gap-3 bg-background/70 px-4 py-2 rounded-lg border shadow-md">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm font-medium">Saving…</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
